/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useEffect, useMemo, useState } from 'react';
import {
	Button,
	CardActions,
	CardContent,
	Divider,
	FormHelperText,
	CircularProgress,
	FormLabel,
	Container,
	Stack,
	Grid,
	useTheme,
} from '@mui/material';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { useClientStorageHooks } from '@/hooks/useClientStorageHooks';
import { useGetUserDetailsQuery } from '@/features/user/userApi';
import { useDispatch, useSelector } from 'react-redux';
import { validationSchema } from './validationSchema';
import { default as folderValidationSchema } from '@/components/dialogs/AddFolderSharepointDialog/validationSchema';
import { useCreateEndorsementMutation } from '@/features/deals/dealsApi';
import { PageHeader } from '@/components/layouts/PageHeader';
import { showToast } from '@/features/toast/toastSlice';
import { useLocation } from 'react-router-dom';
import { FormikErrorScroll } from '@/hooks/useFormikHelper';
import { reset } from '@/features/select/clientSelectSlice';
import { OwnerSelect } from '@/components/selects/OwnerSelect';
import {
	BROKER_GROUP_ID,
	DEAL_OVERVIEW,
	YEAR_FOLDER,
	OWNER_DOC_ID,
} from '../CreateNewBusinessForm/validationSchema';
import { paths } from '@/app/routes';
import { getPipelineIcon } from '@/utils/constants';
import { ASSIGNED_TO_HELP_TEXT } from '@/utils/helperText';
import DealNaming from '../fields/DealNaming';
import { FOLDER_NAME } from '@/components/dialogs/AddFolderSharepointDialog/validationSchema';
import { getYearFolderFormat } from '@/utils/date';
import { uniqueId } from 'lodash';
import { usePoliciesSelect } from '@/context/policiesContext';
import DealFormMessages from '@/components/forms/CreateFormFields/DealFormMessages';
import DealFormActions from '@/components/forms/CreateFormFields/DealFormActions';
import { useGetClientRowQuery } from '@/features/clients/clientsApi';
import { PolicySelectionSync } from '../CreateRenewalForm/PolicySelectionSync';
import { useSyncBrokerGroupFromClient } from '@/hooks/useSyncBrokerGroupFromClient';
import { useLocationStatePolicies } from '@/hooks/useLocationStatePolicies';
import NotesField from '@/components/forms/CreateFormFields/NotesField';
import FileUploadField from '@/components/forms/CreateFormFields/FileUploadField';
import ClientSearchField from './ClientSearchField';

const endorsementValidationSchema = validationSchema.omit(['policyId']).shape({
	policies: Yup.array().min(1, 'At least one policy is required'),
});

export const CreateEndorsementDealForm = () => {
	const dispatch = useDispatch();
	const location = useLocation();
	const { clearSelectedPolicies, setSelectedPolicies } = usePoliciesSelect();

	const account = useSelector((state) => state.msalAccount.account);

	const existingClient = useMemo(() => location?.state?.client ?? {}, [location?.state?.client]);
	const existingClientId = useMemo(() => existingClient?.clientId ?? '', [existingClient]);

	const { existingPolicies } = useLocationStatePolicies(existingClientId);

	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const [formLoading, setFormLoading] = useState(false);
	const [brokerGroupId, setBrokerGroupId] = useState(userDetails?.brokerGroupId);

	useEffect(() => {
		if (!brokerGroupId && userDetails?.brokerGroupId)
			setBrokerGroupId(userDetails?.brokerGroupId);
	}, [brokerGroupId, userDetails?.brokerGroupId]);

	useEffect(() => {
		if (existingPolicies.length === 0) return;
		setSelectedPolicies(existingPolicies);
	}, [existingPolicies, setSelectedPolicies]);

	const initialValues = useMemo(() => {
		const values = {
			// Context is source of truth; PolicySelectionSync copies selectedPolicies here
			policies: [],
			endorsementsFeature: false,
			files: [],
			notes: '',
			[DEAL_OVERVIEW]: '',
			clientName: existingClient?.name ?? '',
			successMessage: '',
			errorMessage: '',
			dealName: '',
			[FOLDER_NAME]: '',
			[YEAR_FOLDER]: getYearFolderFormat(existingPolicies[0]?.FromDate),
			clientId: existingClientId,
			[OWNER_DOC_ID]: userDetails?.userId,
			[BROKER_GROUP_ID]: existingClient?.brokerGroupId ?? userDetails?.brokerGroupId,
		};
		return values;
	}, [existingPolicies, existingClient, existingClientId, userDetails?.userId, userDetails?.brokerGroupId]);

	const [schema, setSchema] = useState(endorsementValidationSchema);

	const { setUpDealFilesAndFolders } = useClientStorageHooks(brokerGroupId);

	const [createEndorsement] = useCreateEndorsementMutation();

	const createEndorsementFolders = async (values, dealId, noteId) => {
		try {
			await setUpDealFilesAndFolders({
				dealId,
				noteId,
				clientId: values.clientId,
				clientName: values.clientName,
				dealName: values[FOLDER_NAME],
				pipelineId: process.env.REACT_APP_PIPELINE_ENDORSEMENTS,
				files: values.files,
				notes: values.notes,
				yearFolder: values.yearFolder,
			});
		} catch (error) {
			console.log('🚀 ~ Error creating endorsement folders', error);
			return {
				error: {
					message: `Endorsement ${dealId} created, but there was an error creating endorsement folder or uploading files`,
				},
			};
		}
	};

	const createNewEndorsement = async (values) => {
		const body = {
			ownerDocId: values[OWNER_DOC_ID] ?? userDetails?.userId,
			dealName: values[FOLDER_NAME].trim(),
			dealOverview: (values[DEAL_OVERVIEW] ?? '').trim(),
			notes: values.notes,
			clientId: values.clientId,
			policies: values.policies.map((p) => ({
				policyId: p.Id ?? p.policyId ?? p.insightId,
				policyTransId: p.policyTransId,
			})),
			brokerGroupId: values[BROKER_GROUP_ID],
		};

		return await createEndorsement(body)
			.unwrap()
			.catch((err) => {
				console.log('🚀 ~ Error creating endorsement', err);
				return { error: { message: 'Error creating endorsement' } };
			});
	};

	return (
		<Formik
			initialValues={initialValues}
			validateOnChange={false}
			validationSchema={schema}
			enableReinitialize
			onSubmit={async (values, { resetForm, setFieldValue, setSubmitting }) => {
				setFormLoading(true);
				console.log('📋 ~ Formik values', values);
				const createError = (message) => {
					setFieldValue('errorMessage', message ?? 'Error creating endorsement');
					setFieldValue('successMessage', '');
					setSubmitting(false);
					setFormLoading(false);
				};

				const toastId = uniqueId();
				dispatch(
					showToast({
						toastId,
						message: 'Creating endorsement...',
						action: {
							path: `${paths.endorsements}`,
							label: 'GO BACK TO ALL ENDORSEMENTS',
						},
						autohide: false,
					}),
				);

				const res = await createNewEndorsement(values);
				if (res?.error) {
					console.log('🙅 ~ Error creating endorsement', res?.error);
					createError(res?.error?.message);
					return;
				}
				const response = await createEndorsementFolders(values, res.dealId, res.noteId);
				if (response?.error) {
					createError(response?.error?.message);
					dispatch(
						showToast({
							toastId,
							message:
								response?.error?.message ??
								`${values[FOLDER_NAME]} Endorsement created`,
							action: {
								path: `${paths.endorsements}/${res.dealId}`,
								label: 'GO TO ENDORSEMENT',
							},
							autohide: false,
						}),
					);
					return;
				}
				console.log('🚀 ~ Successfully created new Endorsement', res);
				dispatch(
					showToast({
						toastId,
						message: `${values[FOLDER_NAME]} Endorsement created!`,
						action: {
							path: `${paths.endorsements}/${res.dealId}`,
							label: 'GO TO ENDORSEMENT',
						},
						autohide: false,
					}),
				);

				const isOnCreateEndorsement =
					window.location.pathname.includes('/create-endorsement');

				if (isOnCreateEndorsement) {
					clearSelectedPolicies();
					resetForm();
					dispatch(reset());
					setFieldValue('successMessage', 'Successfully created Endorsement');
					setFieldValue('errorMessage', '');
					setSubmitting(false);
					setFormLoading(false);
				}
			}}
		>
			{({ isSubmitting }) => (
				<CreateEndorsementDealFormBody
					{...{
						loading: isSubmitting || formLoading,
						setSchema,
						setBrokerGroupId,
					}}
				/>
			)}
		</Formik>
	);
};

const CreateEndorsementDealFormBody = ({ loading, setSchema, setBrokerGroupId }) => {
	const theme = useTheme();
	const location = useLocation();

	const account = useSelector((state) => state.msalAccount.account);
	const { setSelectedPolicies } = usePoliciesSelect();

	const existingClient = useMemo(() => location?.state?.client ?? {}, [location?.state]);
	const isExistingClient = useMemo(
		() => Object.keys(existingClient).length > 0,
		[existingClient],
	);

	const { values, isSubmitting, errors, setFieldValue } = useFormikContext();

	const { existingPolicies } = useLocationStatePolicies(values.clientId);

	const email = account?.username;
	const {
		data: userDetails,
		isLoading,
		isUninitialized,
	} = useGetUserDetailsQuery(
		{ email },
		{
			skip: !email,
		},
	);
	const isUserLoading = isLoading || isUninitialized;

	useEffect(() => {
		setSchema(
			endorsementValidationSchema
				.omit([FOLDER_NAME])
				.concat(folderValidationSchema(values.existingFolders, 'Deal name')),
		);
	}, [values.existingFolders]);

	useEffect(() => {
		const dates = existingPolicies
			.map((p) => p?.FromDate ?? p?.policy?.FromDate)
			.filter(Boolean)
			.sort((a, b) => new Date(a) - new Date(b));

		if (dates[0]) {
			setFieldValue(YEAR_FOLDER, getYearFolderFormat(dates[0]));
		}
	}, [existingPolicies]);

	const { data: clientRow } = useGetClientRowQuery(values.clientId, {
		skip: !values.clientId,
	});

	useSyncBrokerGroupFromClient(clientRow, setBrokerGroupId);

	const gridProps = {
		rowSpacing: 1,
		columnSpacing: 2,
	};
	return (
		<Container sx={{ pt: 3 }}>
			<Form>
				<FormikErrorScroll isSubmitting={isSubmitting} />
				<PageHeader
					title={'Add Endorsement'}
					icon={getPipelineIcon(
						process.env.REACT_APP_PIPELINE_ENDORSEMENTS,
						'40px',
						theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
					)}
				/>
				<CardContent>
					{isUserLoading ? (
						<CircularProgress />
					) : (
						<Grid container {...gridProps}>
							<DealNaming />
							<Grid size={'grow'}>
								<ClientSearchField
									disabled={loading || isExistingClient}
									onClear={() => {
										setSelectedPolicies(Array.from(existingPolicies ?? []));
										setFieldValue(YEAR_FOLDER, null);
									}}
								/>
							</Grid>
							<Grid size={4}>
								<FormLabel>{'Deal assignee'}</FormLabel>
								<OwnerSelect
									initialId={values[OWNER_DOC_ID]}
									disabled={isSubmitting}
									showGroup={true}
									onChange={(user) => {
										setFieldValue(OWNER_DOC_ID, user?.id);
									}}
								/>
								<FormHelperText>{ASSIGNED_TO_HELP_TEXT}</FormHelperText>
							</Grid>
							<Grid size={12}>
								<Stack
									direction={'row'}
									spacing={1}
									alignItems={'center'}
									justifyContent={'space-between'}
								>
									<FormLabel
										sx={{ display: 'flex' }}
										error={Boolean(errors.policies)}
									>
										Policies
									</FormLabel>
								</Stack>
								<Stack>
									<FormHelperText sx={{ ml: 0 }} error={Boolean(errors.policies)}>
										Select one or more policies for this endorsement
									</FormHelperText>
									<PolicySelectionSync />
									{errors.policies && (
										<FormHelperText error>{errors.policies}</FormHelperText>
									)}
								</Stack>
							</Grid>
							<Grid size={12}>
								<NotesField loading={loading} />
							</Grid>
							<Grid size={12}>
								<FileUploadField loading={loading} />
							</Grid>
							<Grid size={12}>
								<DealFormMessages />
							</Grid>
						</Grid>
					)}
				</CardContent>
				<Divider />
				<DealFormActions disabled={isUserLoading || loading} />
			</Form>
		</Container>
	);
};
